import { Campaign } from '@shared/types/mylounge';

/**
 * Creates a unified, ordered campaign list for declarative grid rendering.
 *
 * Assembles campaigns in order: [early access campaigns] + [exclusive at position] + [regular campaigns]
 * This allows rendering with a natural loop where each campaign carries its type via plusBenefit.
 *
 * **IMPORTANT CONTRACT:**
 * - Does NOT deduplicate: If same campaign appears in multiple arrays, all instances are included
 * - Mutates internal unified list (input arrays are not modified - uses spread operator)
 * - Invalid exclusive positions (negative or non-integer) prevent exclusive campaign insertion
 * - Out-of-bounds positions append exclusive campaign at array end
 *
 * @param campaigns - Regular campaigns array (early access campaigns excluded here)
 * @param earlyAccessCampaigns - Array of early access campaigns (can be empty)
 * @param exclusiveCampaign - The exclusive campaign to insert (optional, undefined means no insertion)
 * @param exclusivePosition - Zero-based position for exclusive campaign. Must be non-negative integer
 *
 * @returns New unified array: [early access...] + [exclusive] + [regular...]
 * @example
 * createCompleteCampaignList(
 *   [{id: 'r1'}, {id: 'r2'}],
 *   [{id: 'ea1'}],
 *   {id: 'ex1'},
 *   1
 * )
 * // Returns: [{id: 'ea1'}, {id: 'ex1'}, {id: 'r1'}, {id: 'r2'}]
 */
export const createCompleteCampaignList = (
    campaigns: Campaign[],
    earlyAccessCampaigns: Campaign[],
    exclusiveCampaign: Campaign | undefined,
    exclusivePosition: number,
    isEarlyAccessCarousel: boolean,
): Campaign[] => {
    let unifiedList = campaigns;
    if (!isEarlyAccessCarousel) {
        unifiedList = [...earlyAccessCampaigns, ...campaigns];
    }
    // Assemble: early access + normal campaigns

    // Insert exclusive campaign at calculated position if valid
    if (exclusiveCampaign && Number.isInteger(exclusivePosition) && exclusivePosition >= 0) {
        unifiedList.splice(exclusivePosition, 0, exclusiveCampaign);
    }

    return unifiedList;
};
